import { ModuleWithProviders } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { HomeComponent, HomeLandingComponent } from './public/home.component';
import { AuthManagerComponent } from './public/auth/auth-manager.component';
import { SecureHomeComponent, WorkingAccountSelectorComponent, SecureHomeDefaultRouterComponent } from './secured/secure-home.component';
import { RouterAuthorizerService } from './services/router-authorizer.service';
import { NoNavLayoutComponent } from './secured/layouts/no-nav-layout/no-nav-layout.component';
import { SideNavLayoutComponent } from './secured/layouts/side-nav-layout/side-nav-layout.component';
import { ConnectWiseLayoutComponent } from './secured/layouts/connectwise-layout/connectwise-layout.component';
import { SbamLayoutComponent } from './secured/layouts/sbam-layout/sbam-layout.component';
import { AccountType, RoleType } from './models/AccountModel';
import { MspDashboardComponent } from './secured/dashboards/msp-dashboard.component';
import { AdminAccountDashboardComponent } from './secured/dashboards/admin-account-dashboard.component';
import { CompanyAssessmentDashboardComponent } from './secured/dashboards/company-assessment-dashboard.component';
import { AssessmentTemplateDashboardComponent } from './secured/dashboards/assessment-template-dashboard.component';
import { PlanDashboardComponent } from './secured/dashboards/plan-dashboard.component';
import { IndustryComparisonDashboardComponent } from './secured/dashboards/industry-comparison-dashboard.component';
import { PartnerDashboardComponent } from './secured/dashboards/partner-dashboard.component';
import { BrandDashboardComponent } from './secured/dashboards/brand-dashboard.component';
import { UnavailableDashboardComponent } from './secured/expired/unavailable-dashboard.component';
import { SubAccountDashboardComponent } from './secured/dashboards/sub-account-dashboard.component';
import { NotificationDashboardComponent } from './secured/dashboards/notification-dashboard.component';
import { EmailTemplateDashboardComponent } from './secured/dashboards/email-template-dashboard.component';
import { AdminUserDashboardComponent } from './secured/dashboards/admin-user-dashboard.component';
import { ScanConfigurationDashboardComponent } from './secured/admin/scan-configuration-dashboard.component';
import { CompanyCertificationDashboardComponent } from './secured/dashboards/company-certification-dashboard.component';
import { CertificationDashboardComponent } from './secured/dashboards/certification-dashboard.component';
import { AdminRedirectGuard } from './services/admin-redirect-guard';
import { DeviceVulnerabilityScanDashboardComponent } from './secured/dashboards/device-vulnerability-scan-dashboard.component';

const publicRoutes: Routes = [
  {
    path: 'register',
    redirectTo: 'public/register'
  },
  {
    path: 'public',
    component: HomeComponent,
    children: [
      { path: 'login', component: AuthManagerComponent },
      { path: 'logout', component: AuthManagerComponent },
      { path: 'register', component: AuthManagerComponent },
      { path: 'confirm', component: AuthManagerComponent },
      { path: 'forgotPassword', component: AuthManagerComponent },
      { path: 'ssoLogin', component: AuthManagerComponent },
      { path: '', component: HomeLandingComponent }
    ]
  }
];

const securedRoutes: Routes = [
  { path: 'secured/accountSelection',
    canActivate: [RouterAuthorizerService],
    component: WorkingAccountSelectorComponent
  },{
    path: 'secured',
    canActivate: [RouterAuthorizerService],
    canActivateChild: [RouterAuthorizerService],
    component: SecureHomeComponent,
    children: [
      {
        path: 'unavailable',
        component: SideNavLayoutComponent,
        children: [
          { path: '', component: UnavailableDashboardComponent}
        ]
      },{
          path: 'contactAdmin',
          component: NoNavLayoutComponent,
          children: [
            { path: '', component: UnavailableDashboardComponent}
          ]
      },{
        path: 'master',
        component: SideNavLayoutComponent,
        data: { feature: 'DASHBOARD_ENABLED' },
        children: [
          { path: 'companies', component: SubAccountDashboardComponent, data: { feature: 'ALLOW_SUBACCOUNTS' } },
          { path: 'companies/:subAccountId', component: SubAccountDashboardComponent, data: { feature: 'ALLOW_SUBACCOUNTS' } },
          { path: 'companies/:subAccountId/assessment/:assessmentId', component: SubAccountDashboardComponent, data: { feature: 'ALLOW_SUBACCOUNTS' } },
          { path: 'internal/assessments', component: CompanyAssessmentDashboardComponent },
          { path: 'internal/assessments/:assessmentId', component: CompanyAssessmentDashboardComponent },
          { path: 'analytics/dashboard', component: MspDashboardComponent, data: { feature: 'ANALYTICS_ENABLED' } },
          { path: 'analytics/industryComparison', component: IndustryComparisonDashboardComponent, data: { feature: 'ANALYTICS_ENABLED' }  },
          { path: 'vulnerabilityScans/scanTargets', component: DeviceVulnerabilityScanDashboardComponent, data: { feature: 'ALLOW_SCANNER',  showAllDevices: true } },
          { path: 'vulnerabilityScans/scanTargets/:scanTargetId/scanResults', component: DeviceVulnerabilityScanDashboardComponent, data: { feature: 'ALLOW_SCANNER'} },
          { path: 'vulnerabilityScans/scanTargets/:scanTargetId/scanResults/:scanResultId', component: DeviceVulnerabilityScanDashboardComponent, data: { feature: 'ALLOW_SCANNER'} },
          { path: 'certification', component: CompanyCertificationDashboardComponent, data: { feature: 'ALLOW_CERTIFICATION' } },
          { path: 'certification/:assessmentId', component: CompanyCertificationDashboardComponent, data: { feature: 'ALLOW_CERTIFICATION' } },
        ]
      }, {
        path: 'company',
        component: NoNavLayoutComponent,
        data: { notFeature: 'DASHBOARD_ENABLED' },
        children: [
          { path: 'assessment', component: CompanyAssessmentDashboardComponent },
          { path: 'assessment/:assessmentId', component: CompanyAssessmentDashboardComponent },
          { path: '', redirectTo: 'assessment', pathMatch: 'full' }
        ]
      }, {
        path: 'admin',
        component: SideNavLayoutComponent,
        data: { accountType: AccountType.SYSTEM },
        children: [
          { path: 'accounts', component: AdminAccountDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN, RoleType.SUPPORT_ADMIN]]} },
          { path: 'users', component: AdminUserDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN, RoleType.SUPPORT_ADMIN]]} },
          { path: 'certification', component: CertificationDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN, RoleType.CERTIFICATION_ADMIN]]} },
          { path: 'certification/:assessmentId', component: CertificationDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN, RoleType.CERTIFICATION_ADMIN]]} },
          { path: 'notifications', component: NotificationDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: 'emailTemplates', component: EmailTemplateDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: 'assessmentTemplates', component: AssessmentTemplateDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: 'plans', component: PlanDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: 'partners', component: PartnerDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: 'brands', component: BrandDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: 'scanConfigurations', component: ScanConfigurationDashboardComponent, data: {accountType: AccountType.SYSTEM, requiredRoles: [[RoleType.ADMIN]]} },
          { path: '', canActivate: [AdminRedirectGuard], children: [], pathMatch: 'full' }
        ]
      }, {
        path: '', component: SecureHomeDefaultRouterComponent
      }
    ]
  },{
    path: 'connectwise',
    component: ConnectWiseLayoutComponent,
    data: { partnerKey: 'connectwise' },
    children: [
      { path: 'companies/:subAccountId', component: SubAccountDashboardComponent, data: { feature: 'ALLOW_SUBACCOUNTS', allowAllCompanies: false } },
      { path: 'companies/:subAccountId/assessment/:assessmentId', component: SubAccountDashboardComponent, data: { feature: 'ALLOW_SUBACCOUNTS', allowAllCompanies: false } },
      { path: 'internal/assessments', component: CompanyAssessmentDashboardComponent },
      { path: 'internal/assessments/:assessmentId', component: CompanyAssessmentDashboardComponent },
      { path: '', component: ConnectWiseLayoutComponent }
    ]
  },{
    path: 'partner',
    children: [
      {
        path: 'sbam',
        children: [
          {
            path: 'company/:companyId/assessment/:id',
            component: SbamLayoutComponent,
            data: { partnerKey: 'sbam' }
          }
        ]
      }
    ]
  }
];

const routes: Routes = [
  ...publicRoutes,
  ...securedRoutes,
  {
    path: '**',
    redirectTo: '/public'
  }
];

export const AppRoutingProviders: any[] = [];
export const AppRoutingModule: ModuleWithProviders = RouterModule.forRoot(routes, {
  onSameUrlNavigation: 'reload'
});
